import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography, TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	PaginationLoader,
	PopupSnackbar,
	SendEmailModal,
	PageHeaderListActions,
} from "frame/components";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import * as fromHealthSafetyForm from "features/health-safety-forms";
import { HOME_ROUTE } from "features/home/home-routes";
import StaffTrainingCard from "../cards/staff-training-card";
import {
	AppendStaffTrainingForm,
	EditStaffTrainingForm,
	PageHeaderTabs,
} from "features/health-safety-forms";
import { getPageResults, useIsOnline } from "utils";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { initialJobState } from "features/health-and-safety/reducers/health-safety-reducer";
import { CardText } from "features/timesheet/components/timesheet-list";
import InfiniteScroll from "react-infinite-scroll-component";

const useStyles = makeStyles((theme) => ({
	root: {},
	filter: { display: "flex" },
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
}));

const StaffTrainingList = ({
	role,
	staffTrainings,
	onDelete,
	onGetStaffTrainingList,
	currentOrganisationGUID,
	addStaffTrainingForm,
}) => {
	const isOnline = useIsOnline();
	const classes = useStyles();
	const [showEditModal, setShowEditModal] = useState(false);
	const [showViewModal, setShowViewModal] = useState(false);
	const [showAppendModal, setShowAppendModal] = useState(false);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [isSendingEmail, setIsSendingEmail] = useState(false);
	const [selectedItemId, setSelectedItemId] = useState(null);
	const [siteId, setSiteId] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const [previousSaveSuccess, setPreviousSaveSuccess] = useState(false);
	const isInitialMount = useRef(true);

	const [staffTrainingList, setStaffTrainingList] = useState(getPageResults(initialJobState));
	const [currentPage, setCurrentPage] = useState(1);
	const defaultPageSize = 10; // Reduced from 20 to improve initial load performance
	const [totalNumberOfRecords, setTotalNumberOfRecords] = useState(1);
	const [hasMoreStaffTrainings, setHasMoreStaffTrainings] = useState(true);
	const [isDoneFetching, setIsDoneFetching] = useState(false);
	const ref = useRef(null);
	const [height, setHeight] = useState(0);

	const empty = staffTrainingList && staffTrainingList.length <= 0;

	const onEdit = (id) => () => {
		setSelectedItemId(id);
		setShowEditModal(true);
	};
	const onAppend = (id) => () => {
		setSelectedItemId(id);
		setShowAppendModal(true);
	};
	const onView = (id) => () => {
		setSelectedItemId(id);
		setShowViewModal(true);
	};
	const onEmail = (id, siteId) => () => {
		setSelectedItemId(id);
		setSiteId(siteId);
		setShowEmailModal(true);
	};
	const handleApiError = (error) => {
		if (error.response && error.response.status === "401") {
			setErrorMessage("You are not authorized to perform this action.");
		} else if (error.response && error.response.status === "404") {
			setErrorMessage("The record no longer exists.");
		} else {
			setErrorMessage(
				"Something went wrong, please refresh the page and try again."
			);
		}
	};

	const isFetching = staffTrainings.flag === "processing" && isOnline;
	// const isDoneFetching = staffTrainings.flag === "done" || !isOnline;

	const setNewStaffTrainingState = (staffTrainingResult, page) => {
		let newState = initialJobState;

		if (staffTrainingResult && staffTrainingResult.results && staffTrainingResult.results.length > 0) {
			newState = {
				...newState,
				...staffTrainingResult
			};
		}

		newState.refreshList = staffTrainingResult.refreshList;
		newState.flag = "done";
		// If navigating to first page clear the results from staffTrainingList otherwise add to the end of it
		if (page === 1) {
			setStaffTrainingList([...getPageResults(newState)]);
		} else {
			setStaffTrainingList([...staffTrainingList, ...getPageResults(newState)]);
		}
		setIsDoneFetching(true);
	};

	const getStaffTrainings = (
		fromPageToRequest = currentPage, // fromPageToRequest is the page to request from the API,
		recordDeletedOrUpdated = false, // recordDeletedOrUpdated is a boolean to determine whether to overwrite or append to staffTrainingLists
		pageSize = defaultPageSize // pageSize is the number of records to load from the API, can be paramaterised to request more data.
	) => {
		if (totalNumberOfRecords !== staffTrainingList.length || recordDeletedOrUpdated) {
			if (recordDeletedOrUpdated) {
				setHasMoreStaffTrainings(true);
			}

			safetyWiseApi
				.post(`${currentOrganisationGUID}/staff-training/paged`, {
					ascending: false,
					page: fromPageToRequest,
					pageSize,
					orderBy: "createdAt",
					searchKey: searchFilter
				})
				.then((result) => {
					setNewStaffTrainingState(result.data, fromPageToRequest);

					// If more then the defaultPageSize data is requested from first page then dynamically set the current page, else set to fromPageToRequest
					if (pageSize > defaultPageSize && fromPageToRequest === 1) {
						setCurrentPage(pageSize / defaultPageSize);
					} else {
						setCurrentPage(fromPageToRequest);
					}

					setTotalNumberOfRecords(result.data.totalNumberOfRecords);
					if (
						result.data.totalNumberOfRecords ==
						staffTrainingList.length + result.data.results.length
					) {
						setHasMoreStaffTrainings(false);
					}
				})
				.catch((error) => {
					console.error("Error fetching staff trainings:", error);
					setErrorMessage("Failed to load staff trainings. Please try again.");
					setIsDoneFetching(true);
					setHasMoreStaffTrainings(false);
				});
		} else {
			setHasMoreStaffTrainings(false);
		}
	};

	// The InfiniteScroll Component requires a height for it to work  effectively and for it resize relative to the parent.
	useEffect(() => {
		const handleResize = () => {
			if (ref.current) {
				setHeight(ref.current.offsetHeight - 20); // reduce height by 20px otherwise the scrollbar flows past the height of screen.
			}
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// Initial load - wait for currentOrganisationGUID to be available
	useEffect(() => {
		if (currentOrganisationGUID) {
			getStaffTrainings();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOrganisationGUID]);

	// Watch for form save success and refresh list
	useEffect(() => {
		if (addStaffTrainingForm?.save?.success && !previousSaveSuccess) {
			setPreviousSaveSuccess(true);
			// Refresh the list when a save succeeds
			if (selectedItemId) {
				const staffTrainingIndex = staffTrainingList.findIndex((t) => t.id === selectedItemId);
				const pageSizeToLoad =
					staffTrainingIndex >= 0
						? Math.ceil((staffTrainingIndex + 1) / defaultPageSize) * defaultPageSize
						: defaultPageSize;
				getStaffTrainings(1, true, pageSizeToLoad);
			} else {
				getStaffTrainings(1, true);
			}
		} else if (!addStaffTrainingForm?.save?.success && previousSaveSuccess) {
			setPreviousSaveSuccess(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addStaffTrainingForm?.save?.success, previousSaveSuccess]);

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount - initial load is handled by the first useEffect
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			// Reset to first page when search changes
			setCurrentPage(1);
			setStaffTrainingList([]);
			setTotalNumberOfRecords(1);
			setHasMoreStaffTrainings(true);
			setIsDoneFetching(false);
			getStaffTrainings(1, true);
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchFilter]);

	return (
		<Page className={classes.root} title="Staff Training" style={{ height: "100%" }}>
			<div ref={ref} style={{ backgroundColor: "", height: "100%", overflow: "hidden" }}>
				{/* <p>
					{totalNumberOfRecords} {staffTrainingList.length}
				</p> */}
				{/* n.o of records and length of loaded list */}
				<InfiniteScroll
					dataLength={staffTrainingList.length}
					next={() => {
						getStaffTrainings(currentPage + 1);
					}}
					hasMore={hasMoreStaffTrainings}
					loader={<CardText message={"Loading..."} />}
					height={height + "px"}
					scrollThreshold={0.99}
					style={{ paddingBottom: "10px" }}
				>
					<ScrollToTopOnMount />
											<PageHeaderTabs
							title="Staff Training"
							addTabPath={fromHealthSafetyForm.ADD_STAFF_TRAINING_PATH}
							hideListTabButton={true}
							backActionPath={HOME_ROUTE}
						
						titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}>
							{/* START: Search */}
							{showFilters && (
								<div className={classes.gridContainer}>
									<Grid container spacing={2} className={classes.filterGrid}>
										<Grid item xs={12} md={6}>
											<TextField
												className={classes.search}
												id="input-with-icon-textfield"
												variant="outlined"
												label="Search"
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<SearchIcon />
														</InputAdornment>
													),
													endAdornment: searchFilter && (
														<InputAdornment position="end">
															<IconButton
																aria-label="clear search"
																onClick={() => {
																	setSearchFilter("");
																	setCurrentPage(1);
																	setStaffTrainingList([]);
																	setTotalNumberOfRecords(1);
																	setHasMoreStaffTrainings(true);
																	setIsDoneFetching(false);
																	getStaffTrainings(1, true);
																}}
																edge="end"
																size="small"
															>
																<ClearIcon />
															</IconButton>
														</InputAdornment>
													)
												}}
												value={searchFilter}
												onChange={(val) => {
													const { value } = val.target;
													setSearchFilter(value);
													// Real-time search with debounce effect will handle the API call
												}}
												style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
												placeholder={"e.g. Training Topic Name, Participant Name"}
											/>
										</Grid>
									</Grid>
								</div>
							)}
							{/* END: Search */}
						</PageHeaderTabs>
					<PageContent pad>
						{/* <PaginationHeader pagination={staffTrainings} /> */}
						<p style={{ marginTop: 5 }}>
							{staffTrainingList.length > 0 ? totalNumberOfRecords + " records" : "No records loaded"}
						</p>

						{!empty &&
							isDoneFetching &&
							staffTrainingList.map((item, index) => (
								<StaffTrainingCard
									key={index}
									training={item}
									role={role}
									onAppend={onAppend(item.id)}
									onView={onView(item.id)}
									onEdit={onEdit(item.id)}
									onDelete={() => {
										//index + 1 to get position in array, then get the next n(pageSize)TH multiple which will be the page size to load
										const pageSizeToLoad =
										Math.ceil((index + 1) / defaultPageSize) * defaultPageSize;

										safetyWiseApi
											.del(`${currentOrganisationGUID}/staff-training/delete/${item.id}`)
											.then(() => {
												getStaffTrainings(1, true, pageSizeToLoad);
											})
											.catch((error) => {
												console.error("Error deleting staff training:", error);
												handleApiError(error);
											});
									}}
									onEmail={onEmail(item.id, item.siteId)}
								/>
							))}

						{isFetching && <PaginationLoader height={90} pagination={staffTrainings} />}

						{empty && isDoneFetching && (
							<Typography variant="body1">
								There are currently no staff training records listed.
							</Typography>
						)}

						{/* <Pagination
							onGetList={onGetStaffTrainingList}
							pagination={staffTrainings}
							orderBy="createdAt"
							ascending={false}
							searchKey={searchFilter}
						/> */}
					</PageContent>

					{showAppendModal && (
						<AppendStaffTrainingForm
							id={selectedItemId}
							onClose={(submitted = false) => {
								setShowAppendModal(false);
								if (submitted) {
									const staffTrainingIndex = staffTrainingList.findIndex((t) => t.id === selectedItemId);
									const pageSizeToLoad =
										staffTrainingIndex >= 0
											? Math.ceil((staffTrainingIndex + 1) / defaultPageSize) * defaultPageSize
											: defaultPageSize;
									getStaffTrainings(1, true, pageSizeToLoad);
								}
							}}
						/>
					)}
					{showEditModal && (
						<EditStaffTrainingForm
							id={selectedItemId}
							onClose={(submitted = false) => {
								setShowEditModal(false);
								if (submitted) {
									const staffTrainingIndex = staffTrainingList.findIndex((t) => t.id === selectedItemId);
									const pageSizeToLoad =
										staffTrainingIndex >= 0
											? Math.ceil((staffTrainingIndex + 1) / defaultPageSize) * defaultPageSize
											: defaultPageSize;
									getStaffTrainings(1, true, pageSizeToLoad);
								}
							}}
						/>
					)}
					{showViewModal && (
						<EditStaffTrainingForm
							readOnly
							id={selectedItemId}
							onClose={() => setShowViewModal(false)}
						/>
					)}
			<SendEmailModal
				isOpen={showEmailModal}
				title={"Email Staff Training Record"}
				onClose={() => setShowEmailModal(false)}
				onSend={(selectedRecipients) => {
					setIsSendingEmail(true);
					safetyWiseApi
						.post(
							`${currentOrganisationGUID}/staff-training/email/${selectedItemId}`,
							{
								toInternalAddresses:
									selectedRecipients.recipients,
								toExternalAddresses: [
									...selectedRecipients.recipientsOther,
									...selectedRecipients.recipientsCustomerSites,
								],
							}
						)
						.then(() => {
							setSuccessMessage("Emails sent successfully.");
						})
						.catch(handleApiError)
						.finally(() => {
							setIsSendingEmail(false);
							setShowEmailModal(false);
						});
				}}
				isSaving={isSendingEmail}
				selectedItemId={selectedItemId}
				siteId={siteId}
				emailListType={"allCustomerSiteRecord"}
			/>
					<PopupSnackbar
						message={errorMessage}
						coloured
						error
						onExited={() => setErrorMessage("")}
					/>
					<PopupSnackbar
						message={successMessage}
						coloured
						success
						onExited={() => setSuccessMessage("")}
					/>
					{!isDoneFetching && <CardText message={"Loading..."} style={{ marginTop: "20px" }} />}
					{!hasMoreStaffTrainings && <CardText message={"All staff trainings have been loaded."} />}
					{staffTrainingList.length === 0 && isDoneFetching && (
						<CardText message={"No staff trainings to show."} />
					)}
				</InfiniteScroll>
			</div>
		</Page>
	);
};

StaffTrainingList.propTypes = {
	role: PropTypes.string.isRequired,
	staffTrainings: PropTypes.object.isRequired,
	onDelete: PropTypes.func.isRequired,
	onGetStaffTrainingList: PropTypes.func.isRequired,
	currentOrganisationGUID: PropTypes.string,
	addStaffTrainingForm: PropTypes.object,
};

export default StaffTrainingList;
